<template>
  <button 
    v-if="visible"
    :type="mode"
    :name="name"
    :class="{ disabled }" 
    @click="handleClick" 
    @click.middle="handleClick" 
    :style="buttonStyle"
    :disabled="Boolean(disabled)" 
    v-bind="$attrs"
    v-tooltip="tooltip"
  >
    <s-loading v-if="loading" :color="iconColor" size="small" />

    <span v-else class="button-content">
      <template v-if="icon" >
        <s-chip :text="String(badge)" color="error" v-if="badge" :size="badgeSize">
          <s-icon :icon="icon" :size="iconSize" :color="iconColor" />
        </s-chip>

        <s-icon v-else :icon="icon" :size="iconSize" :color="iconColor" />
      </template>

      <b v-if="label">
        {{ label }}
      </b>
    </span>
  </button>
</template>

<script lang="ts" setup>

// TYPES

type ButtonVariant = 'plain' | 'outlined' | 'text' | 'tonal' | 'icon';

// INTERFACES

interface ButtonProps {
  label?: string;
  name?: string;
  visible?: boolean | Function;
  submit?: boolean;
  color?: Color;
  variant?: ButtonVariant;
  width?: string;
  height?: string;
  disabled?: boolean | Function;
  loading?: boolean | Function;
  icon?: Icons;
  iconSize?: Size;
  iconColor?: Color;
  type?: ColorType;
  tooltip?: string;
  paddless?: boolean;
  badge?: number | string | Function;
  badgeSize?: Size;
  href?: string;
  stopPropagation?:boolean;
}

// PROPS

const props = withDefaults(defineProps<ButtonProps>(), {
  visible: true,
  variant: 'plain',
  width: 'auto',
  height: 'auto',
  iconSize: 'small',
  disabled: false,
  loading: false,
  paddless: false,
  type: 'primary',
  submit: false,
  stopPropagation: false,
  badgeSize: 'small',
});

// EMITS

const emit = defineEmits(['click', 'click:middle']);

const mode = props.submit ? 'submit' : 'button';

// COMPUTEDS

const buttonStyle = computed(() => {
  const color = resolvedColor(props.disabled ? 'disabled' : props.color || typeToColor(props.type));
  
  const padding = props.paddless ? '0' : '0.375rem';
  const border = props.variant === 'outlined' ? `1px solid ${color}` : 'none';
  const backgroundColor = props.variant === 'tonal' ? opacityColor(color, 0.3) : props.variant === 'plain' ? color : 'transparent';

  return {
    backgroundColor,
    border,
    color: resolvedColor(iconColor.value),
    ...props.variant === 'icon' && { padding },
    width: props.width,
    height: props.height,
  }
});

const iconColor = computed(() => {
  const { iconColor, type, variant, disabled, color } = props;
  const baseColor: string = color ?? typeToColor(type);

  if (disabled && variant === 'plain') return 'border-disabled';
  else if (disabled) return 'disabled';
  else if (variant === 'plain') return iconColor ?? 'white';
  else if (variant === 'tonal') return iconColor ?? typeToColor(type);
  else if (iconColor) return iconColor;
  else if (color) return color;
  else if (type) return typeToColor(type);
  return baseColor;
});

const handleClick = (e: MouseEvent) => {
  if (props.disabled || props.loading) return;
  if(!props.submit) e.preventDefault();

  if (props.stopPropagation) {
    e.stopPropagation();
  }

  switch (e.button) {
    case 0:
      emit('click');
      break;
    case 1:
      emit('click:middle');
      break;

    default:
      break;
  }
};

</script>

<style scoped>
button {
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 0.375rem 0.625rem;
  border-radius: 4px;
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 500;
  column-gap: 0.375rem;
  opacity: 1;
  transition: opacity var(--ts-fast) ease-out;
}
button:hover {
  opacity: 0.8;
}
button.disabled {
  background-color: var(--disabled);
  /* cursor: not-allowed; */
}
button.disabled:hover {
  opacity: 1;
}
.button-content {
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: 0.375rem;
}
</style>
